import React, { useRef, useMemo } from 'react';
import { Box, Typography } from '@mui/material';

import {
  PageHeader,
  SectionContent,
  GoBackButton,
  StyledList,
  ContactLink
} from '../../components';
import THEME from '../../constants/theme';
import {
  SECTION_SPACING,
  ELEMENT_SPACING,
  SUBSECTION_SPACING
} from '../../constants';
import amcSchemaImage from '../../assets/images/strategies/amc-schema.svg';
import { AMC_CONTENT } from '../../data/content/strategies';
import ROUTES from '../../config/routes';
import SEO from '../../components/SEO';
import { seoData } from '../../data/seoData';

const AMCPage = ({ setRefs }) => {
  const introRef = useRef(null);
  const understandingRef = useRef(null);
  const featuresRef = useRef(null);
  const whoForRef = useRef(null);

  const refs = useMemo(
    () => ({
      intro: introRef,
      understanding: understandingRef,
      features: featuresRef,
      whoFor: whoForRef
    }),
    []
  );

  React.useEffect(() => {
    if (setRefs && typeof setRefs === 'function') {
      setRefs(refs);
    }
  }, [refs, setRefs]);

  return (
    <>
      <SEO
        title={seoData.amc.title}
        content={seoData.amc.content}
        keywords={seoData.amc.keywords}
        canonical={seoData.amc.canonical}
        ogImage={seoData.amc.ogImage}
      />
      <GoBackButton backTo={ROUTES.STRATEGIES.ROOT} backText='Strategies' />
      <PageHeader
        id='hero'
        title={AMC_CONTENT.TITLE}
        align='left'
        sx={{ mb: SECTION_SPACING }}
      />
      <SectionContent
        id='amc-intro'
        ref={introRef}
        initialSpacing={false}
        text={[
          {
            text: AMC_CONTENT.INTRO,
            marginBottom: SUBSECTION_SPACING,
            fontSize: '1.2rem'
          }
        ]}
      />
      <SectionContent
        id='amc-understanding'
        ref={understandingRef}
        title='Understanding Actively Managed Certificates (AMCs)'
        initialSpacing={false}
        text={[
          {
            text: AMC_CONTENT.UNDERSTANDING,
            marginBottom: SUBSECTION_SPACING,
            fontSize: '1.15rem'
          }
        ]}
      />
      <SectionContent
        id='amc-features'
        ref={featuresRef}
        title='Key Features and Benefits'
        initialSpacing={false}
        text={[]}
      />
      <Box
        sx={{
          width: '100%',
          paddingLeft: {
            xs: THEME.SPACING.MEDIUM,
            sm: THEME.SPACING.MEDIUM,
            md: THEME.SPACING.XL
          },
          marginBottom: SUBSECTION_SPACING
        }}
      >
        <StyledList
          variant='default'
          items={AMC_CONTENT.FEATURES_LIST.LIST_ITEMS}
        />
      </Box>
      <SectionContent
        id='amc-who-for'
        ref={whoForRef}
        title='Who Are AMCs For?'
        initialSpacing={false}
        text={[
          {
            text: AMC_CONTENT.WHO_FOR,
            marginBottom: SECTION_SPACING,
            fontSize: '1.15rem'
          }
        ]}
      />
      <Box sx={{ width: '100%', textAlign: 'center', mb: SECTION_SPACING }}>
        <img
          src={amcSchemaImage}
          alt='AMC Schema'
          style={{
            maxWidth: '400px',
            width: '100%',
            height: 'auto',
            borderRadius: '8px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
          }}
        />
      </Box>
      <Box
        sx={{
          borderTop: `1px solid ${THEME.COLORS.NEUTRAL_GRAY}`,
          paddingTop: ELEMENT_SPACING,
          paddingBottom: ELEMENT_SPACING,
          mt: 4,
          mb: 2
        }}
      >
        <Typography
          variant='body2'
          sx={{
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
            fontSize: '1rem',
            fontStyle: 'italic',
            color: THEME.COLORS.NEUTRAL_GRAY,
            lineHeight: 1.6,
            display: 'block'
          }}
        >
          {AMC_CONTENT.FOOTER}
        </Typography>
        <Box mt={ELEMENT_SPACING}>
          <Typography
            variant='body2'
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
              fontSize: '1.2rem',
              color: THEME.COLORS.PRIMARY_BLUE,
              lineHeight: 1.6
            }}
          >
            Talk to an Expert –&nbsp;
            <ContactLink
              style={{
                color: THEME.COLORS.NEUTRAL_GRAY,
                textDecoration: 'underline',
                fontSize: '1rem'
              }}
            >
              Schedule Your meeting
            </ContactLink>
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default AMCPage;
